import React from 'react';
import { Link } from 'react-router-dom';
import { openCookieSettings } from '../CookieConsent';
import './legal.css';

// Terms of Use and Privacy Policy (requirements section 18). The wording is a starting point written
// for how the platform works today; have it reviewed by a lawyer before relying on it.
export const LEGAL_UPDATED = '20 October 2026';
const SUPPORT_EMAIL = 'support@cheers2night.com';

export function TermsContent() {
  return (
    <main className="container section legal">
      <p className="muted">Last updated: {LEGAL_UPDATED}</p>
      <p>
        These terms apply to the Cheers2Night website and Android app ("Cheers2Night", "we", "us"). By using Cheers2Night you agree to them.
        If you don't agree, please don't use the service.
      </p>

      <h2>1. What Cheers2Night is</h2>
      <p>
        Cheers2Night helps you discover restaurants, bars, pubs and cafés, their offers, live music and events. It is a discovery platform
        only: you can browse offers and venue details, call venues and get directions. There is no offer claiming, promo-code redemption,
        table or event booking, or payment on Cheers2Night. <strong>We do not sell, serve or deliver alcohol</strong>, and no alcohol is sold
        through the platform. Venues (our "partners") run their own premises, set their own prices and are solely responsible for what they
        serve and how.
      </p>

      <h2>2. Age and eligibility</h2>
      <ul>
        <li>You must be of legal drinking age in the state where you use the service and where the venue is located, and at least 18 years old to create an account. You don't need an account to browse.</li>
        <li>Venues verify age at entry. They may refuse entry or service if you can't show valid photo ID, and Cheers2Night is not responsible for that decision.</li>
        <li>Offers or events marked with a higher age limit (for example 21+ or 25+) apply as stated.</li>
      </ul>

      <h2>3. Your account</h2>
      <ul>
        <li>An account is optional and only needed for hangouts. Guests sign in with a mobile number and a one-time SMS code. Keep your phone and codes private; you're responsible for activity on your account.</li>
        <li>Give accurate details. We may suspend accounts used for fraud, abuse or harassment.</li>
      </ul>

      <h2>4. Offers and events</h2>
      <ul>
        <li>Cheers2Night doesn't create discounts. Every offer comes from the venue, which confirms it is accurate and lawful and must honour it; each shows when the venue last updated it. We can't guarantee offers, so confirm with the venue before you go.</li>
        <li>We review every offer before it appears, show offers only from venues whose details we have verified, and may remove any misleading, expired or noncompliant listing. Alcohol-related offers, including dining-bill discounts that cover alcohol, are shown only where we have allowed them after legal clearance and the venue's liquor licence has been verified.</li>
        <li>You can't claim, book or pay for anything through Cheers2Night. Entry, reservations and payment are arranged directly with the venue.</li>
        <li>If an offer looks wrong or expired, report it from the offer or write to us; we'll check it with the venue.</li>
      </ul>

      <h2>5. Safety and responsible drinking</h2>
      <ul>
        <li>Follow each venue's rules, dress code and safety rules, including lifeguard instructions at pool parties.</li>
        <li>Never swim, drive or operate machinery after drinking. Know your limits, look after your friends, and use the emergency contact listed on the event page if you need help.</li>
      </ul>

      <h2>6. Hangouts and community conduct</h2>
      <p>
        When you invite people or chat, be respectful. Don't post anything unlawful, harassing, hateful, sexually explicit, or that promotes
        drunk driving or underage drinking. You can block or report people; we may remove content and suspend accounts.
      </p>

      <h2>7. Partners</h2>
      <p>
        Venues that list on Cheers2Night confirm that they hold every licence their activity needs (including excise, FSSAI and local
        permissions for events and pools), that their listings are accurate and lawful, and that they follow advertising rules for
        alcohol. Private pool-party hosts are published only after we verify them. We may reject, suspend or remove any listing. A separate
        partner agreement may also apply.
      </p>

      <h2>8. Liability</h2>
      <p>
        Cheers2Night is provided "as is". To the extent the law allows, we aren't liable for what happens at a venue or event, for a venue's
        refusal of entry or service, or for indirect or consequential losses. Nothing in these terms limits rights you have under the
        Consumer Protection Act, 2019 or other law that can't be excluded.
      </p>

      <h2>9. Changes and contact</h2>
      <p>
        We may update these terms; the date above shows the latest version, and continuing to use the service means you accept it. These
        terms are governed by the laws of India, with courts in Pune, Maharashtra having jurisdiction. Questions or complaints:{' '}
        <a className="gold" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. See also our <Link className="gold" to="/privacy">Privacy Policy</Link>.
      </p>
    </main>
  );
}

export function PrivacyContent() {
  return (
    <main className="container section legal">
      <p className="muted">Last updated: {LEGAL_UPDATED}</p>
      <p>
        This policy explains what personal data Cheers2Night collects, why, and the choices you have. We process personal data in line with
        the Digital Personal Data Protection Act, 2023 and other applicable Indian law.
      </p>

      <h2>1. What we collect</h2>
      <ul>
        <li><strong>Guest account (optional):</strong> mobile number, the display name you choose, and when you confirmed your age. You can browse without one.</li>
        <li><strong>Activity:</strong> hangouts you create or join and chat messages in them, and notifications we send you. Records of offer claims and pool-party bookings made before these features were switched off are kept as described below.</li>
        <li><strong>Location:</strong> if you allow it, your device's location is used to show nearby venues. It's sent with each search and isn't stored in your account.</li>
        <li><strong>Partners:</strong> business, owner and contact details, venue address and map location, photos and listings.</li>
        <li><strong>Messages to us:</strong> what you send through the contact or partner forms.</li>
        <li><strong>Usage data:</strong> if analytics is enabled, pages viewed and basic device information via Google Analytics.</li>
      </ul>

      <h2>2. Why we use it</h2>
      <ul>
        <li>To run your account and sign you in with SMS codes.</li>
        <li>To send hangout updates and other notifications in the app.</li>
        <li>To keep the platform safe: preventing fraud and abuse, handling reports, and keeping an audit trail of admin and partner actions.</li>
        <li>To understand and improve the service.</li>
      </ul>
      <p>We don't sell your personal data or use it for third-party advertising.</p>

      <h2>3. Who we share it with</h2>
      <ul>
        <li>We don't share your personal data with venues.</li>
        <li>Service providers that run parts of the service for us: our SMS gateway, email provider, hosting provider and, if enabled, Google Analytics.</li>
        <li>Authorities, when the law requires it.</li>
      </ul>

      <h2>4. How long we keep it</h2>
      <p>
        We keep account data while your account is active. Audit records, and records of past offer claims and pool-party bookings, are kept
        for up to 3 years for accounting, dispute and fraud-prevention purposes, then deleted or anonymised. One-time sign-in codes expire
        within minutes and are stored only as secure hashes.
      </p>

      <h2>5. Your rights</h2>
      <p>
        You can ask to access, correct or erase your personal data, withdraw consent, or nominate someone to act for you, by writing to{' '}
        <a className="gold" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We'll respond within 30 days. If you're not satisfied, you
        can complain to the Data Protection Board of India.
      </p>

      <h2>6. Security</h2>
      <p>
        Data is sent over encrypted connections, passwords and sign-in codes are stored only as hashes, and access to admin tools is
        restricted and logged. No system is perfectly secure, so please keep your phone and codes private.
      </p>

      <h2 id="cookies">7. Cookies and similar storage</h2>
      <ul>
        <li><strong>Essential (always on):</strong> your browser's local storage keeps you signed in, remembers your age confirmation,
          your cookie choice and your location filter. These don't track you across sites and are needed for the service to work.</li>
        <li><strong>Analytics (only with your consent):</strong> Google Analytics cookies (<code>_ga</code>, <code>_ga_*</code>) measure
          which pages are used, with IP addresses anonymised. They are set only after you choose <em>Accept All Cookies</em>.</li>
      </ul>
      <p>
        Choosing <em>Deny Cookies</em> keeps analytics off and removes its cookies. You can change your choice at any time:{' '}
        <button type="button" className="link-button gold" onClick={openCookieSettings}>Cookie settings</button>.
      </p>

      <h2>8. Children</h2>
      <p>Cheers2Night isn't for anyone under 18 or under the legal drinking age in their state, and we don't knowingly collect their data.</p>

      <h2>9. Changes and contact</h2>
      <p>
        We'll update the date above when this policy changes and tell you in the app about significant changes. Grievance and privacy
        contact: <a className="gold" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>, Cheers2Night, Pune, Maharashtra, India. See also
        our <Link className="gold" to="/terms">Terms of Use</Link>.
      </p>
    </main>
  );
}
